import { getMessaging, getToken, onMessage } from "firebase/messaging";
import app from "./firebaseConfig";
import { toast } from "react-toastify";
import type { CustomNotificationProps } from "./components/base/PushNotification";
import {
  subscribeToNotification,
  unsubscribeFromNotification,
} from "./store/admin/notificationsStore";
import { forceRemoteLogout } from "./utils/tokenUtils";

let messaging: any = null;
try {
  messaging = getMessaging(app);
} catch (error) {
  console.warn("Firebase Messaging not supported:", error);
}
const vapidKey = import.meta.env.VITE_REACT_APP_FIREBASE_VAPID_KEY;

// Flag para evitar toast duplicados de permisos denegados
const PERMISSION_TOAST_KEY = "fcm_permission_toast_shown";
const PERMISSION_DENIED_KEY = "fcm_permission_denied";
const USER_DISABLED_KEY = "fcm_disabled_by_user";

// Lock de concurrencia: si ya hay una solicitud de token en curso para un
// usuario, las siguientes invocaciones reutilizan la misma promesa en lugar
// de disparar otra suscripción. Esto evita tokens duplicados por re-renders,
// React Strict Mode o rehidrataciones del store.
const inflightTokenRequests = new Map<string, Promise<string | null>>();

const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  let device = "Unknown";
  let platform = "Web";

  if (/Windows/.test(userAgent)) {
    device = "Windows PC";
  } else if (/Macintosh/.test(userAgent)) {
    device = "MacOS";
  } else if (/Linux/.test(userAgent) && !/Android/.test(userAgent)) {
    device = "Linux";
  } else if (/Android/.test(userAgent)) {
    device = "Android";
    platform = "Android";
  } else if (/iPhone|iPad/.test(userAgent)) {
    device = "iOS";
    platform = "iOS";
  }

  // Detectar navegador
  if (platform === "Web") {
    if (/Edg\//.test(userAgent)) {
      device += " - Edge";
    } else if (/Chrome/.test(userAgent)) {
      device += " - Chrome";
    } else if (/Firefox/.test(userAgent)) {
      device += " - Firefox";
    } else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
      device += " - Safari";
    }
  }

  return { device, platform, userAgent };
};

export const requestFCMToken = async ({ id }: { id: string }) => {
  if (!messaging) {
    console.warn("Firebase Messaging not available");
    return null;
  }

  // Si ya hay una solicitud en curso para este usuario, reutilizarla.
  const existing = inflightTokenRequests.get(id);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      if (localStorage.getItem(USER_DISABLED_KEY)) {
        return null;
      }

      // Verificar si ya se denegó el permiso y se mostró el toast
      const permissionDenied = localStorage.getItem(PERMISSION_DENIED_KEY);

      if (permissionDenied) {
        return null;
      }

      const permission = await Notification.requestPermission();

      if (permission !== "granted") {
        // Solo mostrar toast si no se ha mostrado antes en esta sesión
        const toastShown = sessionStorage.getItem(PERMISSION_TOAST_KEY);
        if (!toastShown) {
          toast.error("No se ha dado permiso para recibir notificaciones", {
            toastId: "fcm-permission-denied", // Prevenir duplicados
          });
          sessionStorage.setItem(PERMISSION_TOAST_KEY, "true");
        }
        localStorage.setItem(PERMISSION_DENIED_KEY, "true");
        return null;
      }

      // Limpiar flags si el permiso fue otorgado
      localStorage.removeItem(PERMISSION_DENIED_KEY);
      sessionStorage.removeItem(PERMISSION_TOAST_KEY);

      await navigator.serviceWorker.register("/firebase-messaging-sw.js");

      const currentToken = await getToken(messaging, { vapidKey });

      // Verificar si el token ya está registrado para ESTE usuario en este dominio
      const storedToken = localStorage.getItem("tokenCFM");
      const storedDomain = localStorage.getItem("tokenCFM_domain");
      const storedUserId = localStorage.getItem("tokenCFM_userId");
      const currentDomain = window.location.origin;

      if (
        storedToken === currentToken &&
        storedDomain === currentDomain &&
        storedUserId === id
      ) {
        return currentToken;
      }

      // Obtener info del dispositivo
      const deviceInfo = getDeviceInfo();

      const result = await subscribeToNotification(id, currentToken, deviceInfo);
      if (!result) {
        return null;
      }

      localStorage.setItem("tokenCFM", currentToken);
      localStorage.setItem("tokenCFM_domain", currentDomain);
      localStorage.setItem("tokenCFM_userId", id);
      
      return currentToken;
    } catch (error) {
      console.error("Error al obtener token FCM:", error);
      return null;
    }
  })();

  inflightTokenRequests.set(id, promise);

  try {
    const result = await promise;
    return result;
  } finally {
    inflightTokenRequests.delete(id);
  }
};

export const unsubscribeFCMToken = async ({ id }: { id: string }) => {
  const tokenCFM = localStorage.getItem("tokenCFM");
  try {
    if (tokenCFM) {
      await unsubscribeFromNotification(id, tokenCFM);
    }
  } finally {
    localStorage.setItem(USER_DISABLED_KEY, "true");
    localStorage.removeItem("tokenCFM");
    localStorage.removeItem("tokenCFM_domain");
    localStorage.removeItem("tokenCFM_userId");
  }
};

export const onMessageListener = (
  NotificationComponent: (props: CustomNotificationProps) => React.ReactNode,
) => {
  if (!messaging) {
    console.warn("Firebase Messaging not available");
    return;
  }

  onMessage(messaging, (payload) => {
    const { data } = payload;
    if (!data) return;

    // Interceptar señales de cierre de sesión
    if (data.type === "session_closed") {
      handleRemoteSessionClosedSignal(data);
      return; // No mostrar el toast genérico
    }

    toast(
      NotificationComponent({
        title: data.title,
        body: data.body,
        image: data.image,
        url: data.url,
        icon: data.icon,
        type: (data.type as "report" | "notification") || "notification",
        notificationId: data.notificationId || data.id || "",
        reportId:
          data.reportId || (data.type === "report" ? data.id : undefined),
      }),
      {
        position: "top-right",
        autoClose: false,
        closeButton: false,
        hideProgressBar: true,
        closeOnClick: false,
        draggable: true,
        style: {
          width: "300px",
          padding: "0",
          margin: "auto",
          marginTop: "10px",
        },
      },
    );
  });
};

/**
 * Maneja la señal de cierre remoto de sesión desde el backend
 * @param data Datos de la notificación con sessionId o closedSessionIds
 */
export const handleRemoteSessionClosedSignal = (data: Record<string, any>) => {
  const mySessionId = localStorage.getItem("sessionId");

  // Determinar qué sesiones fueron cerradas
  const closedIds = (data.closedSessionIds
    ? String(data.closedSessionIds).split(",")
    : [data.sessionId]
  ).filter(Boolean);

  // Si la sesión actual no está en la lista de cerradas, ignorar
  if (!mySessionId || !closedIds.includes(mySessionId)) {
    return;
  }

  // La sesión actual fue cerrada remotamente — ejecutar logout forzado
  forceRemoteLogout(
    data.title || "Sesión cerrada",
    data.body || "Tu sesión fue cerrada de forma remota."
  );
};

/**
 * Escucha mensajes del Service Worker sobre cierre de sesiones
 * Usado cuando la sesión se cierra mientras la app está en background
 */
export const listenForSessionClosedSignal = () => {
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "SESSION_CLOSED_SIGNAL") {
      handleRemoteSessionClosedSignal(event.data);
    }
  });
};

/**
 * Listens for NOTIFICATION_CLICK messages sent by the Service Worker
 * when the user clicks a background notification while the app is open.
 * Uses window.location.href for internal routes (handled by React Router on load).
 */
export const listenForSwNavigation = () => {
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "NOTIFICATION_CLICK") {
      const url: string = event.data.url || "/";
      const isExternal =
        url.startsWith("http") && !url.startsWith(window.location.origin);

      if (isExternal) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const path = url.startsWith("http") ? new URL(url).pathname : url;
        window.location.href = path;
      }
    }
  });
};
